import type { AccountInfo, LoginStatusEnum, UserInfo } from "@peerup/auth-kit"

import { envConfig } from "@peerup/host-env"
import { SuiteAdapter } from "@peerup/suite/adapter"
import { getStorageId } from "@peerup/utils"
import { defineStore, getActivePinia } from "pinia"
import { toRaw } from "vue"

import type { currentScene, JoinAccount, RegisterForm, SceneHistoryItem, ShowType } from "@/types/login.types"

import { LoginTypeEnum } from "@/enums/statusEnum"
import $logger from "@/utils/logger"

/**
 * 用户状态接口
 */
interface UserState {
  token: string
  /** 是否30天免登 */
  keepAlive: boolean
  browserKey: string
  showType: ShowType // 登录步骤
  loginType: LoginTypeEnum // 登录方式：email/phone/reset
  account: string // 登录账号
  areaCode: string // 区号
  agreement: boolean // 同意协议
  successList: AccountInfo[] // 组织列表
  failedList: AccountInfo[]
  currentOrg: JoinAccount | null // 当前组织
  inviteCode: string // 邀请码
  selectedScene: string // 选中的场景
  sceneHistory: SceneHistoryItem[] // 场景历史记录
  isMicroApp: boolean // 是否是微前端环境
  currentScene: currentScene // 当前情景
  projectGuid: string // 项目guid 组织邀请链接 项目邀请链接用
  registerForm: RegisterForm // 注册表单
  userInfo: Pick<UserInfo, "userName" | "email"> // 用户信息
  inputName: string // 输入姓名
  setPassword: {
    password: string
  }
  setPhone: {
    phone: string
  }
  tokenSetTime: number
}

// 创建一个标记，确保 browserKey 只初始化一次
let browserKeyInitialized = false

export const defaultUserState: Pick<UserState, "keepAlive" | "loginType" | "agreement"> = {
  keepAlive: !envConfig.VITE_APP_CLIENT,
  loginType: LoginTypeEnum.EMAIL,
  agreement: !!envConfig.HIDE_AGREEMENT_CHECKBOX,
}

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    token: "",
    ...defaultUserState,
    browserKey: "",
    showType: "",
    account: "",
    areaCode: "",
    successList: [],
    currentOrg: null,
    inviteCode: "",
    selectedScene: "",
    sceneHistory: [], // 前进后退栈结构
    isMicroApp: SuiteAdapter.isMicroApp(),
    currentScene: "login",
    failedList: [],
    projectGuid: "",
    registerForm: {
      userName: "",
      orgName: "",
      industry: "",
      identity: "",
      source: "",
    },
    userInfo: {
      userName: "",
      email: "",
    },
    inputName: "",
    setPassword: {
      password: "",
    },
    setPhone: {
      phone: "",
    },
    tokenSetTime: 0,
  }),
  getters: {
    /**
     * 获取有效 token，超时自动清空
     */
    validToken(state): string {
      if (!state.token) return ""
      if (Date.now() - state.tokenSetTime > 10 * 60 * 1000) {
        // 不能直接改 state，需在外部调用 updateState 清空
        return ""
      }
      return state.token
    },
    isEmailLogin(state): boolean {
      return state.loginType === LoginTypeEnum.EMAIL
    },
    isSmsLogin(state): boolean {
      return state.loginType === LoginTypeEnum.SMS
    },
    isFeishuLogin(state): boolean {
      return state.loginType === LoginTypeEnum.FEISHU
    },
  },
  actions: {
    setLoginType(type: LoginTypeEnum) {
      this.loginType = type
    },
    /**
     * 获取并设置浏览器唯一标识
     * 只在首次调用时生成，后续复用已生成的值
     */
    async fetchBrowserKey(): Promise<string> {
      if (!this.browserKey) {
        this.browserKey = await getStorageId() as string
      }
      return this.browserKey
    },

    /**
     * 更新用户状态
     */
    updateState<K extends keyof UserState>(state: Partial<Pick<UserState, K>>) {
      // 1. 保证 sceneHistory 至少有一个初始场景
      if (this.sceneHistory.length === 0 && !this.isMicroApp) {
        this.sceneHistory.push({ showType: "init", currentScene: "login" })
      }

      if ("showType" in state) {
        $logger.log("@peerup/accounts 当前页面", { showType: this.showType, state })
        const newShowType = state.showType as ShowType
        // 2. showType 为 init 时重置 sceneHistory
        if (newShowType === "init") {
          this.sceneHistory = [{ showType: "init", currentScene: "login" }]
        } else if (this.sceneHistory[this.sceneHistory.length - 1]?.showType !== newShowType) {
          // 3. 只 push 不重复的 showType
          this.sceneHistory.push({ showType: newShowType, currentScene: this.currentScene })
        }

        // 4. set-password 特殊处理
        if (newShowType === "set-password") {
          this.setPassword = { password: "" }
        }
        $logger.log("@peerup/accounts 历史栈", { sceneHistory: toRaw(this.sceneHistory) })
      }

      if ("token" in state && typeof state.token === "string") {
        if (state.token) {
          this.tokenSetTime = Date.now()
        } else {
          this.tokenSetTime = 0
        }
      }

      const ignoreKeys = ["loginType"]

      Object.entries(state).forEach(([key, value]) => {
        if (ignoreKeys.includes(key)) return
        (this as unknown as UserState)[key as K] = value as UserState[K]
      })
    },
    /**
     * 更新账号登录类型
     * @param memberGuid 账号唯一标识
     * @param loginStatus 登录状态
     */
    updateAccountLoginStatus(memberGuid: string, loginStatus: LoginStatusEnum) {
      this.successList.forEach(item => {
        if (item.memberGuid === memberGuid) {
          item.loginStatus = loginStatus
        }
      })
      this.failedList.forEach(item => {
        if (item.memberGuid === memberGuid) {
          item.loginStatus = loginStatus
        }
      })
    },

    /**
     * 重置状态
     * 注意：重置时保留 browserKey
     */
    resetState() {
      const browserKey = this.browserKey
      this.$reset()
      this.browserKey = browserKey
      $logger.log("[accounts]重置resetState")
      sessionStorage.removeItem("@peerup/accounts-user")
    },

    /**
     * 初始化 store
     * 在 store 创建时自动调用
     */
    async init(): Promise<void> {
      if (!browserKeyInitialized) {
        browserKeyInitialized = true
        await this.fetchBrowserKey()
      }
    },

    /**
     * 检查并清理过期 token
     */
    clearExpiredToken() {
      if (this.token && Date.now() - this.tokenSetTime > 10 * 60 * 1000) {
        this.updateState({ token: "" })
      }
    },
  },
  persist: {
    key: "@peerup/accounts-user",
    storage: sessionStorage,
    debug: true,
    omit: [
      "isMicroApp",
      "successList",
      "failedList",
      "account",
      "agreement",
    ],
  },
})

/**
 * 在组件setup函数外使用
 */
export function getUserStore() {
  const pinia = getActivePinia()

  if (!pinia) return null
  return useUserStore(pinia)
}
